/**
 * Port and airport layer renderer -- matching Worldmonitor 1:1.
 * Ports: ScatterplotLayer dots colored by port type (naval, oil, container, etc.)
 * Airports: IconLayer with plane icon, purple color.
 *
 * MIT License -- see THIRD-PARTY-NOTICES.md
 */

import { ScatterplotLayer, IconLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';
import { PORTS } from '@/config/data';
import type { Port } from '@/config/data';
import { createSvgIcon } from './icon-layer';

/** Layer IDs handled by this renderer. */
export const PORT_LAYER_IDS = new Set(['ports', 'airports']);

/**
 * Port type -> color mapping matching Worldmonitor exactly.
 * WM reference: DeckGLMap.ts createPortsLayer()
 */
function getPortFillColor(d: Port): [number, number, number, number] {
  switch (d.type) {
    case 'naval':     return [100, 150, 255, 200];
    case 'oil':       return [255, 140, 0, 200];
    case 'lng':       return [255, 200, 50, 200];
    case 'container': return [0, 200, 255, 180];
    case 'mixed':     return [150, 200, 150, 180];
    case 'bulk':      return [180, 150, 120, 180];
    default:          return [0, 200, 255, 160];
  }
}

/** Airport icon -- purple plane, matching Worldmonitor. */
const AIRPORT_ICON = createSvgIcon('plane', [160, 100, 255]);

/** Create deck.gl layers for port markers -- ScatterplotLayer dots. */
function createPortDotLayers(): Layer[] {
  return [
    new ScatterplotLayer<Port>({
      id: 'ports-layer',
      data: PORTS,
      getPosition: (d: Port) => [d.lon, d.lat],
      getRadius: 6000,
      getFillColor: (d: Port) => getPortFillColor(d),
      radiusMinPixels: 4,
      radiusMaxPixels: 10,
      pickable: true,
    }),
  ];
}

/** Create deck.gl layers for airport markers -- IconLayer. */
function createAirportIconLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const alphaScale = Math.min(1, (zoom - 2.5) / 2.5);
  const a = Math.round(160 * Math.max(0.3, alphaScale));

  return [
    new IconLayer<NormalizedEvent>({
      id: 'airport-icons',
      data: events,
      pickable: true,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getIcon: () => ({
        url: AIRPORT_ICON,
        width: 48,
        height: 48,
        anchorY: 48,
      }),
      getSize: 11,
      sizeScale: 1,
      sizeMinPixels: 6,
      sizeMaxPixels: 14,
      sizeUnits: 'pixels' as const,
      opacity: a / 255,
    }),
  ];
}

/** Create deck.gl layers for port or airport data. */
export function createPortLayers(
  layerId: string,
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  if (layerId === 'airports') {
    return createAirportIconLayers(events, zoom);
  }
  // Ports use static typed data from @/config/data -- events param ignored
  return createPortDotLayers();
}
